import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { CircularProgress, styled, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { GridApi } from 'ag-grid-community';
import { FC, useMemo, useState } from 'react';
import { FullScreenContainer } from 'src/components/atoms/FullScreenContainer/FullScreenContainer';
import { StorefrontTypeSelect } from 'src/components/molecules/StorefrontTypeSelect/StorefrontTypeSelect';
import { ActionButtons } from 'src/components/organisms/ActionButtons/ActionsButtons';
import SettingsDropdown from 'src/components/organisms/ActionButtons/SettingsDropdown';
import { DataTable } from 'src/components/organisms/DataTable/DataTable';
import { SmartModuleTitleCard } from 'src/components/organisms/SmartModuleTitleCard/SmartModuleTitleCard';
import { TableWrapper } from 'src/components/styled/TableWrapper';
import { Module, ScopeEnum } from 'src/domain';
import { useChainId } from 'src/hooks';
import { useDiscardChangesModule } from 'src/hooks/discard-changes/useDiscardChangesModule';
import {
    StoreInventoryEditStep,
    useStoreInventorySaveChanges,
} from 'src/hooks/store-inventory-management/save-changes/useStoreInventorySaveChanges';
import { useStoreInventoryUnsavedChanges } from 'src/hooks/store-inventory-management/save-changes/useStoreInventoryUnsavedChanges';
import { useStoreInventorySimulationRows } from 'src/hooks/store-inventory-management/table/useStoreInventorySimulationRows';
import { useStoreInventoryHandleCellChanges } from 'src/hooks/store-inventory-management/useStoreInventoryHandleCellChanges';
import { useStoreInventoryHandleZoneCellChanges } from 'src/hooks/store-inventory-management/useStoreInventoryHandleZoneCellChanges';
import { columns, defaultColDef } from './data/columns';

export const StoreInventoryManagement: FC = () => {
    const chainId = useChainId();
    const [fullscreen, setFullscreen] = useState(false);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [editStep, setEditStep] = useState<StoreInventoryEditStep>('options');
    const [unsavedChanges] = useStoreInventoryUnsavedChanges();
    const discardChanges = useDiscardChangesModule(
        Module.StoreInventoryManagement,
        ScopeEnum.GLOBAL,
        chainId ?? undefined
    );
    const [saveChanges, { loading: isSaving }] = useStoreInventorySaveChanges(editStep);
    const {
        data: simulationRows,
        furnitureColumns,
        furnitureZoneColumns,
        furnitureTypeColorPalette,
        loading,
    } = useStoreInventorySimulationRows(editStep === 'zones');
    const handleOptionCellChanges = useStoreInventoryHandleCellChanges();
    const handleZoneCellChanges = useStoreInventoryHandleZoneCellChanges();
    const hasUnsavedChanges = unsavedChanges.size > 0;
    const allColumns = useMemo(
        () => [...columns, ...(editStep === 'options' ? furnitureColumns : furnitureZoneColumns)],
        [editStep, furnitureColumns, furnitureZoneColumns]
    );
    const onCellValuesChanged = editStep === 'options' ? handleOptionCellChanges : handleZoneCellChanges;

    if (loading) {
        return (
            <Stack
                sx={{
                    alignItems: 'center',
                }}
            >
                <CircularProgress />
            </Stack>
        );
    }

    return (
        <Stack
            sx={{
                gap: 2,
                height: 1,
            }}
        >
            <SmartModuleTitleCard />
            <FullScreenContainer style={{ flex: 1 }} fullscreen={fullscreen}>
                <Stack
                    direction="row"
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Stack
                        direction="row"
                        sx={{
                            gap: 4,
                            alignItems: 'center',
                        }}
                    >
                        <StorefrontTypeSelect />

                        <ToggleButtonGroup
                            exclusive
                            value={editStep}
                            onChange={(_, value: StoreInventoryEditStep | null) => {
                                if (value) {
                                    setEditStep(value);
                                }
                            }}
                            aria-label="Store inventory management steps"
                        >
                            <ToggleButton
                                value="options"
                                color="primary"
                                disabled={hasUnsavedChanges && editStep !== 'options'}
                            >
                                Number of options
                            </ToggleButton>
                            <ToggleButton
                                value="zones"
                                color="primary"
                                disabled={hasUnsavedChanges && editStep !== 'zones'}
                            >
                                Zones
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <Stack
                            direction="row"
                            sx={{
                                alignItems: 'center',
                                gap: 2,
                            }}
                        >
                            <Typography variant="overline">Legend</Typography>
                            {Object.entries(furnitureTypeColorPalette ?? {}).map(([typeName, color]) => (
                                <Box
                                    key={typeName}
                                    sx={{ backgroundColor: color, display: 'inline-block', px: 2, py: 1 }}
                                >
                                    <Typography variant="caption">{typeName}</Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Stack>

                    <ActionButtons
                        hasUnsavedChanges={hasUnsavedChanges}
                        loading={loading}
                        saveLoading={isSaving}
                        discardDisabled={!hasUnsavedChanges || loading || isSaving}
                        onDiscard={discardChanges}
                        onSave={saveChanges}
                        saveDisabled={!hasUnsavedChanges || loading || isSaving}
                        hideAuditLogButton
                    >
                        <ToggleButtonGroup>
                            <Tooltip
                                title={fullscreen ? 'Close fullscreen' : 'Open fullscreen'}
                                className="fullscreen-button"
                            >
                                <ToggleButton
                                    value="fullscreen"
                                    selected={fullscreen}
                                    color="primary"
                                    onClick={() => setFullscreen((prev) => !prev)}
                                >
                                    {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                </ToggleButton>
                            </Tooltip>
                        </ToggleButtonGroup>
                        <SettingsDropdown gridApi={gridApi} module={Module.StoreInventoryManagement} />
                    </ActionButtons>
                </Stack>
                <StyledTableWrapper className="ag-theme-alpine-custom-compact small-font">
                    <DataTable
                        key={editStep}
                        onGridReady={(event) => {
                            setGridApi(event.api);
                        }}
                        onCellValuesChanged={onCellValuesChanged}
                        sideBar={{
                            position: 'left',
                            toolPanels: [
                                {
                                    id: 'filters',
                                    labelDefault: 'Filters',
                                    labelKey: 'filters',
                                    iconKey: 'filter',
                                    toolPanel: 'agFiltersToolPanel',

                                    toolPanelParams: {
                                        suppressExpandAll: false,
                                        className: 'ag-filter-toolpanel-button',
                                    },
                                },
                                {
                                    id: 'columns',
                                    labelDefault: 'Columns',
                                    labelKey: 'columns',
                                    iconKey: 'columns',
                                    toolPanel: 'agColumnsToolPanel',
                                    toolPanelParams: {
                                        suppressExpandAll: false,
                                    },
                                },
                            ],
                        }}
                        headerHeight={250}
                        defaultColDef={defaultColDef}
                        isNumberTable={editStep === 'options'}
                        rows={simulationRows}
                        columns={allColumns}
                        autoGroupColumnDef={columns[0]}
                        domLayout="normal"
                        suppressAggFuncInHeader
                    />
                </StyledTableWrapper>
            </FullScreenContainer>
        </Stack>
    );
};

const StyledTableWrapper = styled(TableWrapper)`
    .store-inventory-zone-cell--editable {
        cursor: pointer;
    }

    .store-inventory-zone-cell--readonly {
        background-color: ${({ theme }) => theme.palette.action.disabledBackground} !important;
        color: ${({ theme }) => theme.palette.text.disabled};
        cursor: not-allowed;
    }

    .ag-header-cell-label {
        justify-content: center;
        align-items: flex-end;
        padding-block-start: 0.5rem;
        padding-block-end: 0.5rem;
    }

    .rotate-header-cell {
        .ag-header-cell-label {
            /* Necessary to allow for text to grow vertically */
            height: 100%;
            writing-mode: sideways-lr;
            justify-content: flex-start;
            align-items: center;
            display: flex;
            padding-block-start: 0;
            padding-block-end: 0;
            padding-inline-start: 0.5rem;
            padding-inline-end: 0.5rem;
            gap: 1rem;

            .ag-sort-indicator-icon {
                padding-left: 0rem;

                &[data-ref="eSortAsc"] {
                    transform: rotate(90deg);
                }

                &[data-ref="eSortDesc"] {
                    transform: rotate(90deg);
                }
            }
        }
    }
`;
